import React from 'react';
import {
    Alert,
    Button,
    Card,
    CardContent,
    Chip,
    LinearProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';

import { ageBucketMeta } from '../../../dto/openInvoice';
import { formatDays, fmtCurrency, fmtDate } from './openInvoiceFormatters';

export default function OpenInvoiceTable({
    loading,
    error,
    sortedInvoices,
    onExport,
    onToggleSort,
}) {
    return (
        <Card sx={{ flex: 1 }}>
            <CardContent sx={{ position: 'relative', pb: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Typography variant="h6">Offene Forderungen</Typography>
                    <Tooltip title="Aktuelle Ansicht als CSV exportieren">
                        <span>
                            <Button
                                variant="outlined"
                                startIcon={<DownloadRoundedIcon />}
                                onClick={onExport}
                                disabled={!sortedInvoices.length}
                            >
                                Exportieren
                            </Button>
                        </span>
                    </Tooltip>
                </Stack>
                {loading && <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <TableContainer sx={{ maxHeight: 520 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell onClick={() => onToggleSort('invoiceNumber')} sx={{ cursor: 'pointer' }}>
                                    Rechnungsnummer
                                </TableCell>
                                <TableCell onClick={() => onToggleSort('customerName')} sx={{ cursor: 'pointer' }}>
                                    Kunde
                                </TableCell>
                                <TableCell onClick={() => onToggleSort('invoiceDate')} sx={{ cursor: 'pointer' }}>
                                    Rechnungsdatum
                                </TableCell>
                                <TableCell onClick={() => onToggleSort('dueDate')} sx={{ cursor: 'pointer' }}>
                                    Fällig am
                                </TableCell>
                                <TableCell onClick={() => onToggleSort('daysOverdue')} sx={{ cursor: 'pointer' }}>
                                    Status
                                </TableCell>
                                <TableCell>
                                    Bucket
                                </TableCell>
                                <TableCell>
                                    Mahnstufe
                                </TableCell>
                                <TableCell align="right" onClick={() => onToggleSort('outstandingAmount')} sx={{ cursor: 'pointer' }}>
                                    Offener Betrag
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!sortedInvoices.length && !loading ? (
                                <TableRow>
                                    <TableCell colSpan={8} align="center">
                                        <Typography variant="body2" color="text.secondary">
                                            Keine offenen Posten für die aktuellen Filter gefunden.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                sortedInvoices.map((invoice) => (
                                    <TableRow key={invoice.id} hover>
                                        <TableCell>{invoice.invoiceNumber}</TableCell>
                                        <TableCell>{invoice.customerName || '—'}</TableCell>
                                        <TableCell>{fmtDate(invoice.invoiceDate)}</TableCell>
                                        <TableCell>{fmtDate(invoice.dueDate)}</TableCell>
                                        <TableCell>{formatDays(invoice.daysOverdue)}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={ageBucketMeta[invoice.ageBucket]?.label || invoice.ageBucket}
                                                size="small"
                                                sx={{
                                                    backgroundColor: `${ageBucketMeta[invoice.ageBucket]?.color}22`,
                                                    color: ageBucketMeta[invoice.ageBucket]?.color || 'inherit',
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>{invoice.dunningLevelName || (invoice.dunningLevelId ? 'Mahnstufe' : 'Keine')}</TableCell>
                                        <TableCell align="right">{fmtCurrency(invoice.outstandingAmount)}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
}
